import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  useReviews,
  useReviewsNewCount,
  useSetReviewApproval,
  useDeleteReview,
  useSetSellerReplyApproval,
  useAdminReply,
  type Review,
} from '@modules/reviews';
import { productsApi } from '@modules/products/api/products.api';
import { productThumbSrc } from '@modules/products/utils';
import type { ProductCardResponse } from '@modules/products/types';
import { Breadcrumbs, OptionsTable, type OptionsTableColumn } from '@components';
import { ActionIcon, Loader } from '@mantine/core';
import {
  Modal,
  DoubleCheckModal,
  CachedImage,
  RefetchButton,
  Textarea,
  Button,
  Pagination,
  QuietSelect,
  Tooltip,
} from '@shared/ui';
import {
  IoCheckmark,
  IoClose,
  IoImageOutline,
  IoStar,
  IoTimeOutline,
} from 'react-icons/io5';
import ReviewsFiltersPopover, {
  readStoredReviewsFilters,
  reviewsFiltersSelectedCount,
  writeStoredReviewsFilters,
  type ReviewsPageFilters,
} from './ReviewsFiltersPopover';
import { formatDate, formatPhoneNumber } from '@shared/utils';
import '@shared/styles/page.css';
import './ReviewsPage.css';

const LIKED_FEATURE_LABELS: Record<string, string> = {
  AS_ON_PICTURE: 'Как на картинке',
  SMELL: 'Запах',
  PACKAGING: 'Упаковка',
  APPEARANCE: 'Внешний вид',
};

const ADMIN_REPLY_MAX = 1000;

function readAdminRole(): 'SUPER_ADMIN' | 'SELLER_ADMIN' | null {
  try {
    const raw = localStorage.getItem('luxus_admin');
    if (!raw) return null;
    const p = JSON.parse(raw) as { role?: string };
    return p.role === 'SELLER_ADMIN'
      ? 'SELLER_ADMIN'
      : p.role === 'SUPER_ADMIN'
        ? 'SUPER_ADMIN'
        : null;
  } catch {
    return null;
  }
}

function getStatusLabel(approved: boolean | null): string {
  if (approved === null) return 'Новый';
  if (approved) return 'Подтверждён';
  return 'Отклонён';
}

function sellerReplyStatusLabel(approved: boolean | null): string {
  if (approved === true) return 'Опубликован';
  if (approved === false) return 'Отклонён';
  return 'На модерации';
}

function ReviewStatusIcon({ approved }: { approved: boolean | null }) {
  const label = getStatusLabel(approved);
  const className =
    approved === true
      ? 'reviews-page-status-approved'
      : approved === false
        ? 'reviews-page-status-rejected'
        : 'reviews-page-status-pending';
  return (
    <Tooltip label={label}>
      <span className={`reviews-page-status-icon ${className}`} aria-label={label}>
        {approved === true ? (
          <IoCheckmark size={18} />
        ) : approved === false ? (
          <IoClose size={18} />
        ) : (
          <IoTimeOutline size={18} />
        )}
      </span>
    </Tooltip>
  );
}

function reviewAuthorName(review: Review): string {
  const u = review.user;
  if (!u) return 'Покупатель';
  return [u.name, u.surname].filter(Boolean).join(' ') || 'Покупатель';
}

const REVIEWS_ADMIN_PAGE_SIZE = 25;

type ModerationFilter = 'all' | 'pending' | 'approved' | 'rejected';

const MODERATION_OPTIONS: { id: ModerationFilter; label: string }[] = [
  { id: 'all', label: 'Все отзывы' },
  { id: 'pending', label: 'На модерации' },
  { id: 'approved', label: 'Подтверждённые' },
  { id: 'rejected', label: 'Отклонённые' },
];

export default function ReviewsPage() {
  const canModerateReviews = readAdminRole() === 'SUPER_ADMIN';
  const { data: newReviewsCount } = useReviewsNewCount();
  const { data: productsRaw } = useQuery({
    queryKey: ['products', 'list', undefined],
    queryFn: () => productsApi.getAll(),
    enabled: !canModerateReviews,
  });
  const products: ProductCardResponse[] = Array.isArray(productsRaw)
    ? productsRaw
    : productsRaw?.products ?? [];
  const setApproval = useSetReviewApproval();
  const setSellerReplyApproval = useSetSellerReplyApproval();
  const adminReply = useAdminReply();
  const deleteReview = useDeleteReview();

  const [reviewModal, setReviewModal] = useState<Review | null>(null);
  const [adminReplyDraft, setAdminReplyDraft] = useState('');
  const [adminReplyConfirmOpen, setAdminReplyConfirmOpen] = useState(false);
  const [deleteReviewTarget, setDeleteReviewTarget] = useState<Review | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [reviewFilters, setReviewFilters] = useState<ReviewsPageFilters>(readStoredReviewsFilters);
  const [moderationFilter, setModerationFilter] = useState<ModerationFilter | null>(null);
  const [page, setPage] = useState(1);
  const filterKey = canModerateReviews
    ? `${moderationFilter ?? ''}:${reviewFilters.sellerIds.join(',')}:${reviewFilters.productIds.join(',')}:${reviewFilters.ratings.join(',')}:${reviewFilters.dateFrom}:${reviewFilters.dateTo}:${reviewFilters.hasPhotos}`
    : `${moderationFilter ?? ''}:${selectedProductId ?? ''}`;
  const [pageFilterKey, setPageFilterKey] = useState(filterKey);
  if (pageFilterKey !== filterKey) {
    setPageFilterKey(filterKey);
    setPage(1);
  }
  const currentPage = pageFilterKey === filterKey ? page : 1;
  const { data: reviewsPage, isLoading, error, refetch, isFetching } = useReviews(
    {
      page: currentPage,
      limit: REVIEWS_ADMIN_PAGE_SIZE,
      moderation: moderationFilter ?? 'all',
      productIds: canModerateReviews
        ? reviewFilters.productIds
        : selectedProductId != null
          ? [selectedProductId]
          : undefined,
      sellerIds: canModerateReviews ? reviewFilters.sellerIds : undefined,
      ratings: canModerateReviews ? reviewFilters.ratings : undefined,
      hasPhotos: canModerateReviews ? reviewFilters.hasPhotos : undefined,
      dateFrom: canModerateReviews ? reviewFilters.dateFrom : undefined,
      dateTo: canModerateReviews ? reviewFilters.dateTo : undefined,
    },
    moderationFilter != null,
  );
  const reviews = reviewsPage?.items ?? [];
  const reviewsTotal = reviewsPage?.total ?? 0;

  const [scopeSearchQuery, setScopeSearchQuery] = useState('');

  const openReviewModal = (review: Review) => {
    setReviewModal(review);
    setAdminReplyDraft('');
    setAdminReplyConfirmOpen(false);
  };

  const closeReviewModal = () => {
    setReviewModal(null);
    setAdminReplyDraft('');
    setAdminReplyConfirmOpen(false);
  };

  const confirmAdminReply = () => {
    if (!reviewModal) return;
    const text = adminReplyDraft.trim();
    if (!text) return;
    adminReply.mutate(
      { id: reviewModal.id, text },
      {
        onSuccess: (updated) => {
          setReviewModal(updated);
          setAdminReplyDraft('');
          setAdminReplyConfirmOpen(false);
        },
      },
    );
  };

  const canEditAdminReply =
    canModerateReviews &&
    reviewModal?.approved === true &&
    !reviewModal?.adminReply;

  useEffect(() => {
    if (!canModerateReviews) return;
    writeStoredReviewsFilters(reviewFilters);
  }, [canModerateReviews, reviewFilters]);

  const productOptions = useMemo(
    () =>
      products.map((product) => {
        const categoryNames =
          product.categories
            ?.map((cat) => cat.category.name)
            .filter(Boolean)
            .join(', ') || '';
        const label = categoryNames ? `${product.name} (${categoryNames})` : product.name;
        return { id: product.id, label };
      }),
    [products],
  );

  const filteredScopeOptions = useMemo(() => {
    const q = scopeSearchQuery.trim().toLowerCase();
    if (!q) return productOptions;
    return productOptions.filter((o) => o.label.toLowerCase().includes(q));
  }, [productOptions, scopeSearchQuery]);

  const selectedScopeLabel = useMemo(() => {
    if (selectedProductId == null) return 'Все товары';
    return productOptions.find((p) => p.id === selectedProductId)?.label ?? 'Все товары';
  }, [selectedProductId, productOptions]);

  const selectedModerationLabel =
    MODERATION_OPTIONS.find((o) => o.id === moderationFilter)?.label ?? 'Все отзывы';

  useEffect(() => {
    if (moderationFilter != null || newReviewsCount == null) return;
    setModerationFilter(newReviewsCount.count > 0 ? 'pending' : 'all');
  }, [moderationFilter, newReviewsCount]);

  const listLoading = moderationFilter == null || isLoading || isFetching;

  const confirmDeleteReview = () => {
    if (!deleteReviewTarget) return;
    deleteReview.mutate(deleteReviewTarget.id, {
      onSettled: () => setDeleteReviewTarget(null),
    });
  };

  const reviewColumns: OptionsTableColumn<Review>[] = useMemo(() => {
    const orderColumn: OptionsTableColumn<Review> = {
      key: 'order',
      label: 'Заказ',
      render: (review: Review) => {
        const orderNumber = review.order?.orderNumber ?? '—';
        if (review.orderId) {
          return (
            <Link
              to={`/orders/${review.orderId}`}
              state={{ from: 'reviews' }}
              className="reviews-page-link"
              onClick={(e) => e.stopPropagation()}
            >
              {orderNumber}
            </Link>
          );
        }
        return orderNumber;
      },
    };

    const shopColumn: OptionsTableColumn<Review> = {
      key: 'shop',
      label: 'Магазин',
      render: (review: Review) => {
        const seller = review.product?.seller;
        if (!seller) {
          return <span title="Товар без привязки к магазину">—</span>;
        }
        return (
          <Link
            to={`/sellers/${seller.id}`}
            state={{ from: 'reviews' }}
            className="reviews-page-link"
            onClick={(e) => e.stopPropagation()}
          >
            {seller.shopName}
          </Link>
        );
      },
    };

    const base: OptionsTableColumn<Review>[] = [
      {
        key: 'approved',
        label: 'Статус',
        style: { width: '88px' },
        render: (review: Review) => {
          if (review.approved === null && canModerateReviews) {
            return (
              <span className="reviews-page-status-with-actions">
                <ReviewStatusIcon approved={null} />
                <span className="reviews-page-actions" onClick={(e) => e.stopPropagation()}>
                  <Tooltip label="Подтвердить">
                    <ActionIcon
                      variant="light"
                      color="green"
                      size="sm"
                      radius="md"
                      aria-label="Подтвердить"
                      onClick={() => setApproval.mutate({ id: review.id, approved: true })}
                    >
                      <IoCheckmark size={18} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Отклонить">
                    <ActionIcon
                      variant="light"
                      color="red"
                      size="sm"
                      radius="md"
                      aria-label="Отклонить"
                      onClick={() => setApproval.mutate({ id: review.id, approved: false })}
                    >
                      <IoClose size={18} />
                    </ActionIcon>
                  </Tooltip>
                </span>
              </span>
            );
          }
          return <ReviewStatusIcon approved={review.approved ?? null} />;
        },
      },
      {
        key: 'rating',
        label: 'Оценка',
        render: (review: Review) => (
          <span className="reviews-page-rating">
            {review.rating}
            <IoStar size={14} className="reviews-page-rating-star" aria-hidden />
          </span>
        ),
      },
      {
        key: 'text',
        label: 'Текст',
        render: (review: Review) => {
          const text = review.text || '—';
          const hasPhotos = review.photos?.length > 0;
          const hasSellerReply = Boolean(review.sellerReply);
          const hasAdminReply = Boolean(review.adminReply);
          return (
            <span className="reviews-page-text-cell">
              {hasPhotos ? (
                <IoImageOutline
                  size={15}
                  className="reviews-page-text-cell-icon"
                  title="Есть фото"
                />
              ) : null}
              <span className="reviews-page-text-cell-text">{text}</span>
              {hasSellerReply ? (
                <span
                  className={`reviews-page-reply-badge${review.sellerReplyApproved === true
                      ? ' reviews-page-reply-badge--ok'
                      : review.sellerReplyApproved === false
                        ? ' reviews-page-reply-badge--rejected'
                        : ' reviews-page-reply-badge--pending'
                    }`}
                  title={sellerReplyStatusLabel(review.sellerReplyApproved)}
                >
                  ответ
                </span>
              ) : null}
              {hasAdminReply ? (
                <span className="reviews-page-reply-badge reviews-page-reply-badge--admin" title="Лавка цветов">
                  лавка
                </span>
              ) : null}
            </span>
          );
        },
      },
      {
        key: 'product',
        label: 'Товар',
        style: { maxWidth: '180px', width: '180px' },
        render: (review: Review) => {
          const name = review.product?.name ?? '—';
          if (review.productId) {
            return (
              <Link
                to={`/products/${review.productId}`}
                state={{ from: 'reviews' }}
                className="reviews-page-link reviews-page-product"
                title={name}
                onClick={(e) => e.stopPropagation()}
              >
                {name}
              </Link>
            );
          }
          return (
            <span className="reviews-page-product" title={name}>
              {name}
            </span>
          );
        },
      },
      {
        key: 'createdAt',
        label: 'Дата',
        render: (review: Review) => formatDate(review.createdAt),
      },
    ];

    if (canModerateReviews) {
      const productIdx = base.findIndex((c) => c.key === 'product');
      const insertAt = productIdx >= 0 ? productIdx + 1 : base.length - 2;
      return [...base.slice(0, insertAt), shopColumn, orderColumn, ...base.slice(insertAt)];
    }
    const productIdx = base.findIndex((c) => c.key === 'product');
    const insertAt = productIdx >= 0 ? productIdx + 1 : base.length - 2;
    return [...base.slice(0, insertAt), orderColumn, ...base.slice(insertAt)];
  }, [canModerateReviews, setApproval]);

  const moderationBusy =
    setApproval.isPending || setSellerReplyApproval.isPending || adminReply.isPending;

  return (
    <div className="page-container reviews-page">
      <Breadcrumbs
        items={[
          { label: 'Главная', path: '/' },
          { label: 'Отзывы' },
        ]}
      />

      <div className="page-header">
        <div className="page-header-top reviews-page-header-top">
          <h1>Отзывы</h1>
          <div className="reviews-page__filters">
            {canModerateReviews ? (
              <ReviewsFiltersPopover
                value={reviewFilters}
                selectedCount={reviewsFiltersSelectedCount(reviewFilters)}
                onChange={setReviewFilters}
              />
            ) : (
              <QuietSelect<number | 'all'>
                className="reviews-page__select-wrap"
                ariaLabel="Фильтр по товару"
                label={selectedScopeLabel}
                value={selectedProductId ?? 'all'}
                search={{
                  value: scopeSearchQuery,
                  onChange: setScopeSearchQuery,
                  placeholder: 'Поиск товара...',
                }}
                onChange={(next) => {
                  setSelectedProductId(next === 'all' ? null : next);
                }}
                options={[
                  ...(!scopeSearchQuery.trim()
                    ? [{
                        value: 'all' as const,
                        label: 'Все товары',
                      }]
                    : []),
                  ...filteredScopeOptions.map((option) => ({
                    value: option.id,
                    label: option.label,
                  })),
                ]}
              />
            )}

            <QuietSelect<ModerationFilter>
              className="reviews-page__select-wrap"
              ariaLabel="Модерация отзывов"
              label={selectedModerationLabel}
              value={moderationFilter}
              onChange={setModerationFilter}
              options={MODERATION_OPTIONS.map((option) => ({
                value: option.id,
                label: option.label,
              }))}
            />
          </div>
        </div>
      </div>

      <div className="content-wrapper reviews-page-content-wrapper">
        {canModerateReviews && moderationBusy ? (
          <div className="reviews-page-approval-overlay" aria-hidden>
            <Loader size="lg" />
          </div>
        ) : null}

        <div className="reviews-table-scroll">
          <div className="reviews-desktop-table">
            <OptionsTable
              columns={reviewColumns}
              data={reviews}
              loading={listLoading}
              error={error}
              refetch={refetch}
              emptyMessage="Нет отзывов"
              getActions={(review: Review) => [
                {
                  label: 'Удалить',
                  onClick: () => setDeleteReviewTarget(review),
                  isDanger: true,
                },
              ]}
              onRowClick={(review) => openReviewModal(review)}
            />
          </div>

          <div className="reviews-mobile-list" aria-label="Список отзывов">
            {error && refetch ? (
              <RefetchButton
                refetch={refetch}
                isLoading={listLoading}
                errorMessage={
                  error instanceof Error ? error.message : 'Не удалось загрузить данные'
                }
              />
            ) : listLoading ? (
              <div className="empty-container">
                <Loader />
              </div>
            ) : reviews.length === 0 ? (
              <div className="reviews-page__empty" role="status">
                Нет отзывов
              </div>
            ) : (
              reviews.map((review) => {
                const productName = review.product?.name ?? 'Товар';
                const hasPhotos = (review.photos?.length ?? 0) > 0;
                return (
                  <button
                    key={review.id}
                    type="button"
                    className="reviews-mobile-card"
                    onClick={() => openReviewModal(review)}
                  >
                    <div className="reviews-mobile-card__top">
                      <span
                        className="reviews-mobile-card__rating"
                        aria-label={`Оценка ${review.rating} из 5`}
                      >
                        {Array.from({ length: 5 }, (_, i) => (
                          <IoStar
                            key={i}
                            size={14}
                            className={
                              i < review.rating
                                ? 'reviews-page-modal__star reviews-page-modal__star--on'
                                : 'reviews-page-modal__star'
                            }
                            aria-hidden
                          />
                        ))}
                        <span className="reviews-mobile-card__rating-num">{review.rating}</span>
                      </span>
                      <ReviewStatusIcon approved={review.approved ?? null} />
                    </div>

                    <div className="reviews-mobile-card__product">
                      {productName}
                      {hasPhotos ? (
                        <IoImageOutline
                          size={14}
                          className="reviews-mobile-card__photo-icon"
                          aria-label="Есть фото"
                        />
                      ) : null}
                      {review.sellerReply ? (
                        <span className="reviews-mobile-card__reply-mark">ответ</span>
                      ) : null}
                      {review.adminReply ? (
                        <span className="reviews-mobile-card__reply-mark">лавка</span>
                      ) : null}
                    </div>

                    <p className="reviews-mobile-card__text">
                      {review.text?.trim() || 'Без текста'}
                    </p>

                    <div className="reviews-mobile-card__footer">
                      <span className="reviews-mobile-card__date">
                        {formatDate(review.createdAt)}
                      </span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
        <Pagination
          page={currentPage}
          onChange={setPage}
          totalItems={reviewsTotal}
          pageSize={REVIEWS_ADMIN_PAGE_SIZE}
        />
      </div>

      <DoubleCheckModal
        isOpen={deleteReviewTarget !== null}
        onClose={() => setDeleteReviewTarget(null)}
        title="Удалить отзыв?"
        message="Отзыв будет удалён без возможности восстановления."
        firstButtonText="Отмена"
        secondButtonText="Удалить"
        secondButtonAction={confirmDeleteReview}
        isLoading={deleteReview.isPending}
      />

      <Modal
        isOpen={reviewModal !== null}
        onClose={closeReviewModal}
        title="Отзыв"
        maxWidth={560}
      >
        {reviewModal ? (
          <div className="reviews-page-modal">
            <Link
              to={`/products/${reviewModal.productId}`}
              state={{ from: 'reviews' }}
              className="reviews-page-modal__product"
              onClick={closeReviewModal}
            >
              {productThumbSrc(reviewModal.product?.images?.[0]) ? (
                <div className="reviews-page-modal__product-image">
                  <CachedImage
                    src={productThumbSrc(reviewModal.product?.images?.[0]) || ''}
                    alt={reviewModal.product?.name ?? 'Товар'}
                    loading="lazy"
                  />
                </div>
              ) : (
                <div
                  className="reviews-page-modal__product-image reviews-page-modal__product-image--empty"
                  aria-hidden
                />
              )}
              <div className="reviews-page-modal__product-info">
                <span className="reviews-page-modal__product-name">
                  {reviewModal.product?.name ?? 'Товар'}
                </span>
                <span className="reviews-page-modal__product-hint">Открыть карточку товара</span>
              </div>
            </Link>

            <div className="reviews-page-modal__meta">
              <span
                className="reviews-page-modal__rating"
                aria-label={`Оценка ${reviewModal.rating} из 5`}
              >
                {Array.from({ length: 5 }, (_, i) => (
                  <IoStar
                    key={i}
                    size={16}
                    className={
                      i < reviewModal.rating
                        ? 'reviews-page-modal__star reviews-page-modal__star--on'
                        : 'reviews-page-modal__star'
                    }
                    aria-hidden
                  />
                ))}
                <span className="reviews-page-modal__rating-num">{reviewModal.rating}</span>
              </span>
              <span className="reviews-page-modal__meta-sep" aria-hidden>
                ·
              </span>
              <span className="reviews-page-modal__meta-text">
                {formatDate(reviewModal.createdAt)}
              </span>
              {reviewModal.orderId ? (
                <>
                  <span className="reviews-page-modal__meta-sep" aria-hidden>
                    ·
                  </span>
                  <Link
                    to={`/orders/${reviewModal.orderId}`}
                    state={{ from: 'reviews' }}
                    className="reviews-page-modal__order-link"
                    onClick={closeReviewModal}
                  >
                    Заказ {reviewModal.order?.orderNumber ?? reviewModal.orderId}
                  </Link>
                </>
              ) : null}
              {canModerateReviews && reviewModal.product?.seller ? (
                <>
                  <span className="reviews-page-modal__meta-sep" aria-hidden>
                    ·
                  </span>
                  <Link
                    to={`/sellers/${reviewModal.product.seller.id}`}
                    state={{ from: 'reviews' }}
                    className="reviews-page-modal__order-link"
                    onClick={closeReviewModal}
                  >
                    {reviewModal.product.seller.shopName}
                  </Link>
                </>
              ) : null}
            </div>

            <div className="reviews-page-modal__author">
              <span className="reviews-page-modal__author-name">
                {reviewAuthorName(reviewModal)}
              </span>
              {reviewModal.user?.phone ? (
                <span className="reviews-page-modal__author-phone">
                  {formatPhoneNumber(reviewModal.user.phone)}
                </span>
              ) : null}
              <span
                className={
                  reviewModal.approved === true
                    ? 'reviews-page-status-approved'
                    : reviewModal.approved === false
                      ? 'reviews-page-status-rejected'
                      : 'reviews-page-status-pending'
                }
              >
                {getStatusLabel(reviewModal.approved ?? null)}
              </span>
              {canModerateReviews && reviewModal.approved === null ? (
                <span className="reviews-page-actions">
                  <Tooltip label="Подтвердить">
                    <ActionIcon
                      variant="light"
                      color="green"
                      size="sm"
                      radius="md"
                      aria-label="Подтвердить"
                      loading={setApproval.isPending}
                      onClick={() => {
                        setApproval.mutate(
                          { id: reviewModal.id, approved: true },
                          { onSuccess: (updated) => setReviewModal(updated) },
                        );
                      }}
                    >
                      <IoCheckmark size={18} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Отклонить">
                    <ActionIcon
                      variant="light"
                      color="red"
                      size="sm"
                      radius="md"
                      aria-label="Отклонить"
                      loading={setApproval.isPending}
                      onClick={() => {
                        setApproval.mutate(
                          { id: reviewModal.id, approved: false },
                          { onSuccess: (updated) => setReviewModal(updated) },
                        );
                      }}
                    >
                      <IoClose size={18} />
                    </ActionIcon>
                  </Tooltip>
                </span>
              ) : null}
            </div>

            {reviewModal.likedFeatures?.length > 0 ? (
              <div className="reviews-page-modal__tags">
                {reviewModal.likedFeatures.map((feature) => (
                  <span key={feature} className="reviews-page-modal__tag">
                    {LIKED_FEATURE_LABELS[feature] ?? feature}
                  </span>
                ))}
              </div>
            ) : null}

            <div className="reviews-page-modal__text">
              {reviewModal.text?.trim() || 'Без текста'}
            </div>

            {reviewModal.photos && reviewModal.photos.length > 0 ? (
              <div className="reviews-page-modal__photos">
                <div className="reviews-page-modal__photos-title">Фото от покупателя</div>
                <div className="reviews-page-modal__photos-grid">
                  {reviewModal.photos.map((url) => (
                    <a
                      key={url}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="reviews-page-modal__photo"
                    >
                      <CachedImage src={url} alt="" loading="lazy" />
                    </a>
                  ))}
                </div>
              </div>
            ) : null}

            {reviewModal.sellerReply ? (
              <div className="reviews-page-modal__seller-reply">
                <div className="reviews-page-modal__seller-reply-title">Ответ магазина</div>
                <div
                  className={`reviews-page-modal__seller-reply-status${reviewModal.sellerReplyApproved === true
                      ? ' reviews-page-modal__seller-reply-status--ok'
                      : reviewModal.sellerReplyApproved === false
                        ? ' reviews-page-modal__seller-reply-status--rejected'
                        : ' reviews-page-modal__seller-reply-status--pending'
                    }`}
                >
                  {sellerReplyStatusLabel(reviewModal.sellerReplyApproved)}
                  {reviewModal.sellerRepliedAt
                    ? ` · ${formatDate(reviewModal.sellerRepliedAt)}`
                    : ''}
                </div>
                <div className="reviews-page-modal__seller-reply-body">
                  <div
                    className={`reviews-page-modal__seller-reply-text${reviewModal.sellerReplyApproved === false
                        ? ' reviews-page-modal__seller-reply-text--rejected'
                        : ''
                      }`}
                  >
                    {reviewModal.sellerReply}
                  </div>
                  {canModerateReviews && reviewModal.sellerReplyApproved === null ? (
                    <div className="reviews-page-modal__seller-reply-actions">
                      <Tooltip label="Опубликовать ответ">
                        <ActionIcon
                          variant="light"
                          color="green"
                          size="sm"
                          radius="md"
                          aria-label="Опубликовать ответ"
                          loading={setSellerReplyApproval.isPending}
                          onClick={() => {
                            setSellerReplyApproval.mutate(
                              { id: reviewModal.id, approved: true },
                              { onSuccess: (updated) => setReviewModal(updated) },
                            );
                          }}
                        >
                          <IoCheckmark size={18} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Отклонить ответ">
                        <ActionIcon
                          variant="light"
                          color="red"
                          size="sm"
                          radius="md"
                          aria-label="Отклонить ответ"
                          loading={setSellerReplyApproval.isPending}
                          onClick={() => {
                            setSellerReplyApproval.mutate(
                              { id: reviewModal.id, approved: false },
                              { onSuccess: (updated) => setReviewModal(updated) },
                            );
                          }}
                        >
                          <IoClose size={18} />
                        </ActionIcon>
                      </Tooltip>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {canModerateReviews ? (
              <div className="reviews-page-modal__seller-reply">
                <div className="reviews-page-modal__seller-reply-title">Лавка цветов</div>
                {reviewModal.adminReply ? (
                  <>
                    <div className="reviews-page-modal__seller-reply-status reviews-page-modal__seller-reply-status--ok">
                      Опубликован
                      {reviewModal.adminRepliedAt
                        ? ` · ${formatDate(reviewModal.adminRepliedAt)}`
                        : ''}
                    </div>
                    <div className="reviews-page-modal__seller-reply-text">
                      {reviewModal.adminReply}
                    </div>
                  </>
                ) : reviewModal.approved === true ? (
                  <>
                    <Textarea
                      placeholder="Напишите ответ от имени Лавки цветов…"
                      value={adminReplyDraft}
                      onChange={(e) =>
                        setAdminReplyDraft(e.target.value.slice(0, ADMIN_REPLY_MAX))
                      }
                      minRows={3}
                      maxRows={8}
                      autosize
                    />
                    <div className="reviews-page-modal__seller-reply-footer">
                      <span className="reviews-page-modal__seller-reply-hint">
                        Ответ сразу появится у покупателя.
                      </span>
                      <span className="reviews-page-modal__seller-reply-counter">
                        {adminReplyDraft.trim().length}/{ADMIN_REPLY_MAX}
                      </span>
                    </div>
                    <Button
                      type="button"
                      disabled={!adminReplyDraft.trim() || adminReply.isPending || !canEditAdminReply}
                      loading={adminReply.isPending}
                      onClick={() => setAdminReplyConfirmOpen(true)}
                    >
                      Ответить
                    </Button>
                  </>
                ) : (
                  <div className="reviews-page-modal__seller-reply-hint">
                    Ответить можно после подтверждения отзыва.
                  </div>
                )}
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>

      <DoubleCheckModal
        isOpen={adminReplyConfirmOpen}
        onClose={() => setAdminReplyConfirmOpen(false)}
        title="Отправить ответ?"
        message="Ответ от имени Лавки цветов сразу появится у покупателя. Изменить его будет нельзя."
        firstButtonText="Отмена"
        secondButtonText="Отправить"
        secondButtonAction={confirmAdminReply}
        isLoading={adminReply.isPending}
      />
    </div>
  );
}
